import React, { useState, useEffect } from 'react';
import {
  Button,
  Select,
  Radio,
  Input,
  Row,
  Col,
  message,
  Space,
  InputNumber,
  Modal,
  DatePicker,
  Form,
  Alert
} from 'antd';
import {
  ClockCircleOutlined,
  LogoutOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import locale from 'antd/locale/zh_CN';
import { ConfigProvider } from 'antd';

// 配置dayjs插件和语言
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('zh-cn');

// 设置默认时区为北京时间
dayjs.tz.setDefault("Asia/Shanghai");
import api from '@/services/api';
import { TodayStatus, Project, WorkRecordCreate } from '@/types';

const { Option } = Select;
const { TextArea } = Input;

// 请假类型配置
const LEAVE_TYPES = [
  { value: 'annual_leave', label: '年假' },
  { value: 'sick_leave', label: '病假' },
  { value: 'personal_leave', label: '事假' },
  { value: 'compensatory_leave', label: '调休' },
  { value: 'marriage_leave', label: '婚假' },
  { value: 'maternity_leave', label: '产假' }
];

// 出差类型配置
const BUSINESS_TRIP_TYPES = [
  { value: 'domestic_trip', label: '国内出差' },
  { value: 'international_trip', label: '国外出差' },
  { value: 'client_visit', label: '客户拜访' },
  { value: 'meeting_trip', label: '会议出差' },
  { value: 'training_trip', label: '培训出差' }
];

const WorkRecords: React.FC = () => {
  const [todayStatus, setTodayStatus] = useState<TodayStatus | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | undefined>(undefined);
  const [description, setDescription] = useState<string>('');
  const [workHours, setWorkHours] = useState<number | undefined>(undefined);

  // Modal 状态
  const [leaveModalVisible, setLeaveModalVisible] = useState<boolean>(false);
  const [businessTripModalVisible, setBusinessTripModalVisible] = useState<boolean>(false);
  const [form] = Form.useForm();

  // 用于处理日期选择的状态
  const [leaveStartDate, setLeaveStartDate] = useState<dayjs.Dayjs | null>(null);
  const [leaveEndDate, setLeaveEndDate] = useState<dayjs.Dayjs | null>(null);
  const [tripStartDate, setTripStartDate] = useState<dayjs.Dayjs | null>(null);
  const [tripEndDate, setTripEndDate] = useState<dayjs.Dayjs | null>(null);
  
  // 出差项目选择
  const [tripSelectedProject, setTripSelectedProject] = useState<number | undefined>(undefined);

  // 成功记录列表
  const [successRecords, setSuccessRecords] = useState<Array<{
    id: string;
    message: string;
    timestamp: string;
    isFromDatabase?: boolean;
  }>>([]);

  useEffect(() => {
    const loadData = async () => {
      await fetchTodayStatus();
      // 获取所有项目
      const allProjectsData = await api.getProjects();
      setProjects(allProjectsData);
      // 获取最近使用的项目
      const recentProjectsData = await api.getRecentProjects();
      setRecentProjects(recentProjectsData);
      await fetchTodayWorkRecords(allProjectsData);
    };
    loadData();
  }, []);

  // 添加成功记录的函数
  const addSuccessRecord = (message: string) => {
    const newRecord = {
      id: Date.now().toString(),
      message: message,
      timestamp: dayjs().format('HH:mm:ss')
    };
    setSuccessRecords(prev => [newRecord, ...prev]);
  };

  const fetchProjects = async () => {
    try {
      const allProjectsData = await api.getProjects();
      setProjects(allProjectsData);
      const recentProjectsData = await api.getRecentProjects();
      setRecentProjects(recentProjectsData);
    } catch (error) {
      console.error('获取项目失败:', error);
    }
  };

  const fetchTodayWorkRecords = async (projectsList?: Project[]) => {
    try {
      const today = dayjs().format('YYYY-MM-DD');
      
      // 获取今日所有记录（工作、请假、出差）
      const allRecords = await api.getWorkRecords({
        start_date: today,
        end_date: today
      });
      
      
      const currentProjects = projectsList || projects;
      
      // 转换所有记录为成功记录格式
      const todayRecords = allRecords.map(record => {
        const startTime = dayjs.utc(record.start_time).tz("Asia/Shanghai");
        const endTime = dayjs.utc(record.end_time).tz("Asia/Shanghai");
        
        let message = '';
        let timestamp = startTime.format('HH:mm:ss');
        
        if (record.record_type === 'work') {
          const projectName = currentProjects.find(p => p.id === record.project_id)?.name || '未知项目';
          const duration = endTime.diff(startTime, 'hour', true);
          message = `工作记录：${projectName}，时长：${duration.toFixed(1)}小时，内容：${record.description}`;
        } else if (record.record_type === 'leave') {
          const leaveTypeName = LEAVE_TYPES.find(t => t.value === record.leave_type)?.label || '请假';
          const dateRange = endTime.isSame(startTime, 'day') ? 
            startTime.format('MM月DD日') : 
            `${startTime.format('MM月DD日')} - ${endTime.format('MM月DD日')}`;
          message = `${leaveTypeName}记录：${dateRange}，${record.description || '无备注'}`;
          timestamp = startTime.format('MM-DD');
        } else if (record.record_type === 'business_trip') {
          const tripTypeName = BUSINESS_TRIP_TYPES.find(t => t.value === record.business_trip_type)?.label || '出差';
          const projectName = currentProjects.find(p => p.id === record.project_id)?.name || '未知项目';
          const dateRange = endTime.isSame(startTime, 'day') ? 
            startTime.format('MM月DD日') : 
            `${startTime.format('MM月DD日')} - ${endTime.format('MM月DD日')}`;
          message = `${tripTypeName}记录：${projectName}，${dateRange}，${record.description || '无备注'}`;
          timestamp = startTime.format('MM-DD');
        }
        
        return {
          id: record.id.toString(),
          message,
          timestamp,
          isFromDatabase: true
        };
      });
      
      setSuccessRecords(todayRecords.reverse()); // 最新的在前
    } catch (error) {
      console.error('获取今日工作记录失败:', error);
    }
  };

  const fetchTodayStatus = async () => {
    try {
      const status = await api.getTodayStatus();
      setTodayStatus(status);
    } catch (error: any) {
      if (error.response?.status !== 404) {
        message.error('获取今日状态失败');
      } else {
        setTodayStatus({ can_check_in: true, can_check_out: false, check_in: undefined, check_out: undefined });
      }
    }
  };

  const handleCheckIn = async () => {
    try {
      const checkInResult = await api.checkIn();
      message.success('签到成功');
      
      // 立即更新本地状态，确保按钮状态正确
      setTodayStatus(prev => ({
        check_in: checkInResult,
        check_out: undefined,
        can_check_in: false,
        can_check_out: true
      }));
      
      // 然后刷新完整状态
      fetchTodayStatus();
    } catch (error: any) {
      message.error(error.response?.data?.detail || '签到失败');
    }
  };

  const handleCheckOut = async () => {
    
    if (!todayStatus?.check_in) {
      message.error('请先签到');
      return;
    }
    try {
      const checkOutResult = await api.checkOut({ check_in_id: todayStatus.check_in.id });
      message.success('签退成功');
      
      // 立即更新本地状态，确保按钮状态正确
      setTodayStatus(prev => prev ? {
        ...prev,
        check_out: checkOutResult,
        can_check_out: false
      } : null);
      
      // 然后刷新完整状态
      fetchTodayStatus();
    } catch (error: any) {
      message.error(error.response?.data?.detail || '签退失败');
    }
  };

  const handleLeave = () => {
    setLeaveModalVisible(true);
    form.resetFields();
    setLeaveStartDate(null);
    setLeaveEndDate(null);
  };

  const handleBusinessTrip = () => {
    setBusinessTripModalVisible(true);
    form.resetFields();
    setTripStartDate(null);
    setTripEndDate(null);
    setTripSelectedProject(undefined);
  };

  // 提交请假申请
  const handleLeaveSubmit = async (values: any) => {
    try {
      const { startDate, endDate, leaveType, description } = values;
      
      // 验证日期
      if (endDate && dayjs(endDate).isBefore(dayjs(startDate))) {
        message.error('结束日期不能早于开始日期');
        return;
      }
      
      const leaveRecord: WorkRecordCreate = {
        record_type: 'leave',
        leave_type: leaveType,
        start_time: dayjs(startDate).tz("Asia/Shanghai").startOf('day').toISOString(),
        end_time: endDate ? dayjs(endDate).tz("Asia/Shanghai").endOf('day').toISOString() : dayjs(startDate).tz("Asia/Shanghai").endOf('day').toISOString(),
        description: description || '',
        status: 'active'
      };

      await api.createWorkRecord(leaveRecord);
      
      message.success('请假记录添加成功！');
      setLeaveModalVisible(false);
      form.resetFields();
      setLeaveStartDate(null);
      setLeaveEndDate(null);
      
      // 刷新今日记录
      await fetchTodayWorkRecords();
    } catch (error: any) {
      console.error('请假申请提交失败:', error);
      console.error('错误详情:', error.response?.data);
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map((err: any) => err.msg || err.message || String(err)).join(', ');
          message.error(`提交失败: ${errorMessages}`);
        } else {
          message.error(`提交失败: ${error.response.data.detail}`);
        }
      } else {
        message.error(`提交失败，请稍后重试 (状态码: ${error.response?.status})`);
      }
    }
  };

  // 提交出差申请
  const handleBusinessTripSubmit = async (values: any) => {
    try {
      const { startDate, endDate, businessTripType, description, projectId } = values;
      
      // 验证项目选择
      if (!projectId && !tripSelectedProject) {
        message.error('请选择出差关联的项目');
        return;
      }
      
      // 验证日期
      if (endDate && dayjs(endDate).isBefore(dayjs(startDate))) {
        message.error('结束日期不能早于开始日期');
        return;
      }
      
      const tripRecord: WorkRecordCreate = {
        record_type: 'business_trip',
        business_trip_type: businessTripType,
        project_id: projectId || tripSelectedProject,
        start_time: dayjs(startDate).tz("Asia/Shanghai").startOf('day').toISOString(),
        end_time: endDate ? dayjs(endDate).tz("Asia/Shanghai").endOf('day').toISOString() : dayjs(startDate).tz("Asia/Shanghai").endOf('day').toISOString(),
        description: description || '',
        status: 'active'
      };

      await api.createWorkRecord(tripRecord);
      
      message.success('出差记录添加成功！');
      setBusinessTripModalVisible(false);
      form.resetFields();
      setTripStartDate(null);
      setTripEndDate(null);
      setTripSelectedProject(undefined);
      
      // 刷新今日记录
      await fetchTodayWorkRecords();
    } catch (error: any) {
      console.error('出差申请提交失败:', error);
      console.error('错误详情:', error.response?.data);
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map((err: any) => err.msg || err.message || String(err)).join(', ');
          message.error(`提交失败: ${errorMessages}`);
        } else {
          message.error(`提交失败: ${error.response.data.detail}`);
        }
      } else {
        message.error(`提交失败，请稍后重试 (状态码: ${error.response?.status})`);
      }
    }
  };

  const handleAddWork = async () => {
    if (!selectedProject) {
      message.error('请选择项目');
      return;
    }
    if (!description.trim()) {
      message.error('请输入工作内容');
      return;
    }
    if (!workHours || workHours <= 0) {
      message.error('请输入有效的工作时长');
      return;
    }

    try {
      const now = dayjs().tz("Asia/Shanghai");
      const workRecord = {
        record_type: 'work' as const,
        work_type: 'project_work' as const,
        project_id: selectedProject,
        start_time: now.toISOString(),
        end_time: now.add(workHours, 'hour').toISOString(),
        description: description.trim(),
        status: 'active' as const,
      };
      
      const createdRecord = await api.createWorkRecord(workRecord);
      const selectedProjectName = projects.find(p => p.id === selectedProject)?.name || '未知项目';
      
      addSuccessRecord(
        `工作记录添加成功！项目：${selectedProjectName}，时长：${workHours}小时，内容：${description.trim()}`
      );
      setDescription('');
      setSelectedProject(undefined);
      setWorkHours(undefined);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '添加失败');
    }
  };


  return (
    <ConfigProvider locale={locale}>
    <div style={{ 
      padding: 'min(24px, 4vw)', 
      maxWidth: 'min(800px, 95vw)', 
      margin: '0 auto', 
      backgroundColor: '#f0f2f5', 
      minHeight: '100vh' 
    }}>
      {/* 顶部标题和按钮 */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 'min(24px, 4vw)',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <h1 style={{ 
          fontSize: 'min(24px, 5vw)', 
          fontWeight: 'bold', 
          margin: 0, 
          color: '#333' 
        }}>
          工作记录
        </h1>
        <Space>
          <Button 
            type="primary" 
            onClick={handleLeave}
            style={{ 
              backgroundColor: '#1890ff',
              borderColor: '#1890ff',
              borderRadius: '6px'
            }}
          >
            请假
          </Button>
          <Button 
            type="primary" 
            onClick={handleBusinessTrip}
            style={{ 
              backgroundColor: '#1890ff',
              borderColor: '#1890ff',
              borderRadius: '6px'
            }}
          >
            出差
          </Button>
        </Space>
      </div>



      {/* 签到签退按钮 */}
      <div style={{ 
        backgroundColor: 'white',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Row gutter={16}>
          <Col span={12}>
            <Button
              block
              size="large"
              icon={<ClockCircleOutlined />}
              onClick={handleCheckIn}
              disabled={!todayStatus?.can_check_in}
              style={{ 
                height: 'min(40px, 8vw)',
                borderRadius: '6px',
                backgroundColor: todayStatus?.can_check_in ? '#f0f0f0' : '#e6e6e6',
                fontSize: 'min(14px, 3.5vw)'
              }}
            >
              签到
            </Button>
          </Col>
          <Col span={12}>
            <Button
              block
              size="large"
              icon={<LogoutOutlined />}
              onClick={handleCheckOut}
              disabled={!todayStatus?.can_check_out}
              style={{ 
                height: 'min(40px, 8vw)',
                borderRadius: '6px',
                backgroundColor: todayStatus?.can_check_out ? '#f0f0f0' : '#e6e6e6',
                fontSize: 'min(14px, 3.5vw)'
              }}
            >
              签退
            </Button>
          </Col>
        </Row>
      </div>

      {/* 项目选择 */}
      <div style={{ 
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Select
          placeholder="项目"
          style={{ width: '100%', marginBottom: '16px' }}
          size="large"
          value={selectedProject}
          onChange={setSelectedProject}
          optionFilterProp="children"
          showSearch
          filterOption={(input, option) =>
            (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
          }
        >
          {/* 最近使用的项目 */}
          {recentProjects.length > 0 && (
            <Select.OptGroup label="最近使用">
              {recentProjects.map(project => (
                <Option key={`recent-${project.id}`} value={project.id}>
                  📌 {project.name}
                </Option>
              ))}
            </Select.OptGroup>
          )}
          
          {/* 所有项目 */}
          <Select.OptGroup label="所有项目">
            {projects
              .filter(project => !recentProjects.some(rp => rp.id === project.id)) // 排除已经在最近项目中显示的
              .map(project => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
          </Select.OptGroup>
        </Select>

        {/* 项目圆形选择器 - 显示最近使用的项目 */}
        {recentProjects.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              fontSize: '14px', 
              color: '#666', 
              marginBottom: '8px',
              fontWeight: '500'
            }}>
              最近使用的项目
            </div>
            <Radio.Group 
              value={selectedProject} 
              onChange={(e) => setSelectedProject(e.target.value)}
              style={{ width: '100%', marginBottom: '8px' }}
            >
              <Row gutter={16}>
                {recentProjects.slice(0, 3).map((project, index) => (
                  <Col span={index < 2 ? 12 : 24} key={project.id}>
                    <Radio.Button 
                      value={project.id}
                      style={{ 
                        width: '100%', 
                        textAlign: 'center',
                        borderRadius: '20px',
                        height: '40px',
                        lineHeight: '38px',
                        backgroundColor: selectedProject === project.id ? '#1890ff' : '#f0f0f0',
                        color: selectedProject === project.id ? 'white' : '#333',
                        border: selectedProject === project.id ? '1px solid #1890ff' : '1px solid #d9d9d9'
                      }}
                    >
                      {project.name}
                    </Radio.Button>
                  </Col>
                ))}
              </Row>
            </Radio.Group>
          </div>
        )}

        {/* 工作时长选择 */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            fontSize: 'min(14px, 3.5vw)', 
            color: '#666', 
            marginBottom: '8px',
            fontWeight: '500'
          }}>
            工作时长
          </div>
          <Row gutter={12} align="middle">
            <Col span={16}>
              <Radio.Group 
                value={workHours} 
                onChange={(e) => setWorkHours(e.target.value)}
                style={{ width: '100%' }}
              >
                <Row gutter={8}>
                  <Col span={6}>
                    <Radio.Button value={0.5} style={{ width: '100%', textAlign: 'center' }}>
                      0.5h
                    </Radio.Button>
                  </Col>
                  <Col span={6}>
                    <Radio.Button value={1} style={{ width: '100%', textAlign: 'center' }}>
                      1h
                    </Radio.Button>
                  </Col>
                  <Col span={6}>
                    <Radio.Button value={2} style={{ width: '100%', textAlign: 'center' }}>
                      2h
                    </Radio.Button>
                  </Col>
                  <Col span={6}>
                    <Radio.Button value={4} style={{ width: '100%', textAlign: 'center' }}>
                      4h
                    </Radio.Button>
                  </Col>
                </Row>
              </Radio.Group>
            </Col>
            <Col span={8}>
              <InputNumber
                value={workHours}
                onChange={(value) => setWorkHours(value || undefined)}
                min={0.1}
                max={24}
                step={0.1}
                precision={1}
                style={{ width: '100%' }}
                addonAfter="小时"
                placeholder="自定义"
              />
            </Col>
          </Row>
        </div>

        {/* 文本输入区域 */}
        <TextArea
          rows={5}
          placeholder="请输入工作内容..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={{ 
            marginBottom: 'min(24px, 4vw)',
            borderRadius: '6px',
            fontSize: 'min(14px, 3.5vw)'
          }}
        />

        {/* 添加按钮 */}
        <Button
          type="primary"
          size="large"
          block
          onClick={handleAddWork}
          style={{ 
            height: 'min(48px, 10vw)',
            borderRadius: '6px',
            backgroundColor: '#1890ff',
            borderColor: '#1890ff',
            fontSize: 'min(16px, 4vw)',
            fontWeight: 'bold'
          }}
        >
          添加
        </Button>
      </div>

      {/* 成功记录显示区域 */}
      {successRecords && successRecords.length > 0 ? (
        <div style={{ 
          backgroundColor: 'white',
          borderRadius: '8px',
          marginBottom: 'min(24px, 4vw)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: 'min(16px, 3vw)',
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: '#fafafa'
          }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: 'min(16px, 4vw)', 
              color: '#333',
              fontWeight: '600'
            }}>
              今日工作记录 ({successRecords.length})
            </h3>
          </div>
          <div style={{
            padding: '8px'
          }}>
            {successRecords.map((record) => (
              <div
                key={record.id}
                style={{
                  padding: 'min(12px, 2.5vw) min(16px, 3vw)',
                  margin: 'min(8px, 1.5vw) 0',
                  backgroundColor: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}
              >
                <CheckCircleOutlined 
                  style={{ 
                    color: '#52c41a', 
                    fontSize: '16px',
                    marginTop: '2px'
                  }} 
                />
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: 'min(14px, 3.5vw)', 
                    color: '#333',
                    lineHeight: '1.4',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word'
                  }}>
                    {record.message}
                  </div>
                  <div style={{ 
                    fontSize: 'min(12px, 3vw)', 
                    color: '#999',
                    marginTop: '4px'
                  }}>
                    {record.timestamp}
                  </div>
                </div>
                {!record.isFromDatabase && (
                  <Button
                    type="text"
                    size="small"
                    onClick={() => {
                      setSuccessRecords(prev => prev.filter(r => r.id !== record.id));
                    }}
                    style={{ 
                      color: '#999',
                      padding: '0 4px'
                    }}
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* 请假申请 Modal */}
      <Modal
        title="请假及出差"
        open={leaveModalVisible}
        onCancel={() => setLeaveModalVisible(false)}
        footer={null}
        width="min(500px, 90vw)"
        style={{ top: 'min(50px, 5vh)' }}
      >
        <Form
          form={form}
          onFinish={handleLeaveSubmit}
          layout="vertical"
          style={{ marginTop: 24 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="开始日期"
                name="startDate"
                rules={[{ required: true, message: '请选择开始日期' }]}
              >
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <DatePicker
                    placeholder="请选择日期"
                    style={{ flex: 1 }}
                    format="YYYY年MM月DD日"
                    value={leaveStartDate}
                    onChange={(date) => {
                      setLeaveStartDate(date);
                      form.setFieldValue('startDate', date);
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      const today = dayjs();
                      setLeaveStartDate(today);
                      form.setFieldValue('startDate', today);
                    }}
                    style={{ 
                      fontSize: '12px', 
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d9d9d9'
                    }}
                  >
                    今天
                  </Button>
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="结束日期"
                name="endDate"
                rules={[{ required: false, message: '请选择结束日期' }]}
              >
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <DatePicker
                    placeholder="请选择日期"
                    style={{ flex: 1 }}
                    format="YYYY年MM月DD日"
                    value={leaveEndDate}
                    onChange={(date) => {
                      setLeaveEndDate(date);
                      form.setFieldValue('endDate', date);
                    }}
                    disabled={!leaveStartDate}
                    disabledDate={(current) => {
                      return leaveStartDate ? current.isBefore(leaveStartDate, 'day') : false;
                    }}
                  />
                  <Button
                    size="small"
                    disabled={!leaveStartDate}
                    onClick={() => {
                      const today = dayjs();
                      setLeaveEndDate(today);
                      form.setFieldValue('endDate', today);
                    }}
                    style={{ 
                      fontSize: '12px', 
                      padding: '4px 8px',
                      backgroundColor: !leaveStartDate ? '#f5f5f5' : '#f0f0f0',
                      border: '1px solid #d9d9d9'
                    }}
                  >
                    今天
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
          <div style={{ fontSize: '12px', color: '#1890ff', marginBottom: 16, textAlign: 'center' }}>
            💡 温馨提示：如果是单日请假，可以不选择结束日期
          </div>
          
          <Form.Item
            label="请假类型"
            name="leaveType"
            rules={[{ required: true, message: '请选择请假类型' }]}
          >
            <Select placeholder="年假" style={{ width: '100%' }}>
              {LEAVE_TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            label="说明备注"
            name="description"
          >
            <TextArea
              rows={4}
              placeholder="请输入请假原因..."
              style={{ resize: 'none' }}
            />
          </Form.Item>
          
          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col span={12}>
              <Button 
                block 
                onClick={() => setLeaveModalVisible(false)}
                style={{ height: 40 }}
              >
                取消
              </Button>
            </Col>
            <Col span={12}>
              <Button 
                type="primary" 
                htmlType="submit" 
                block
                style={{ height: 40, backgroundColor: '#1890ff' }}
              >
                确定
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 出差申请 Modal */}
      <Modal
        title="请假及出差"
        open={businessTripModalVisible}
        onCancel={() => setBusinessTripModalVisible(false)}
        footer={null}
        width="min(500px, 90vw)"
        style={{ top: 'min(50px, 5vh)' }}
      >
        <Form
          form={form}
          onFinish={handleBusinessTripSubmit}
          layout="vertical"
          style={{ marginTop: 24 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="开始日期"
                name="startDate"
                rules={[{ required: true, message: '请选择开始日期' }]}
              >
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <DatePicker
                    placeholder="请选择日期"
                    style={{ flex: 1 }}
                    format="YYYY年MM月DD日"
                    value={tripStartDate}
                    onChange={(date) => {
                      setTripStartDate(date);
                      form.setFieldValue('startDate', date);
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => {
                      const today = dayjs();
                      setTripStartDate(today);
                      form.setFieldValue('startDate', today);
                    }}
                    style={{ 
                      fontSize: '12px', 
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #d9d9d9'
                    }}
                  >
                    今天
                  </Button>
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="结束日期"
                name="endDate"
                rules={[{ required: false, message: '请选择结束日期' }]}
              >
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <DatePicker
                    placeholder="请选择日期"
                    style={{ flex: 1 }}
                    format="YYYY年MM月DD日"
                    value={tripEndDate}
                    onChange={(date) => {
                      setTripEndDate(date);
                      form.setFieldValue('endDate', date);
                    }}
                    disabled={!tripStartDate}
                    disabledDate={(current) => {
                      return tripStartDate ? current.isBefore(tripStartDate, 'day') : false;
                    }}
                  />
                  <Button
                    size="small"
                    disabled={!tripStartDate}
                    onClick={() => {
                      const today = dayjs();
                      setTripEndDate(today);
                      form.setFieldValue('endDate', today);
                    }}
                    style={{ 
                      fontSize: '12px', 
                      padding: '4px 8px',
                      backgroundColor: !tripStartDate ? '#f5f5f5' : '#f0f0f0',
                      border: '1px solid #d9d9d9'
                    }}
                  >
                    今天
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
          <div style={{ fontSize: '12px', color: '#1890ff', marginBottom: 16, textAlign: 'center' }}>
            💡 温馨提示：如果是单日出差，可以不选择结束日期
          </div>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="出差类型"
                name="businessTripType"
                rules={[{ required: true, message: '请选择出差类型' }]}
              >
                <Select placeholder="国内出差" style={{ width: '100%' }}>
                  {BUSINESS_TRIP_TYPES.map(type => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="关联项目"
                name="projectId"
                rules={[{ required: true, message: '请选择出差关联的项目' }]}
              >
                <Select 
                  placeholder="请选择项目" 
                  style={{ width: '100%' }}
                  value={tripSelectedProject}
                  onChange={(value) => {
                    setTripSelectedProject(value);
                    form.setFieldValue('projectId', value);
                  }}
                  optionFilterProp="children"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {/* 最近使用的项目 */}
                  {recentProjects.length > 0 && (
                    <Select.OptGroup label="最近使用">
                      {recentProjects.map(project => (
                        <Option key={`recent-trip-${project.id}`} value={project.id}>
                          📌 {project.name}
                        </Option>
                      ))}
                    </Select.OptGroup>
                  )}
                  
                  {/* 所有项目 */}
                  <Select.OptGroup label="所有项目">
                    {projects
                      .filter(project => !recentProjects.some(rp => rp.id === project.id))
                      .map(project => (
                        <Option key={`trip-${project.id}`} value={project.id}>
                          {project.name}
                        </Option>
                      ))}
                  </Select.OptGroup>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            label="说明备注"
            name="description"
          >
            <TextArea
              rows={4}
              placeholder="请输入出差详情..."
              style={{ resize: 'none' }}
            />
          </Form.Item>
          
          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col span={12}>
              <Button 
                block 
                onClick={() => setBusinessTripModalVisible(false)}
                style={{ height: 40 }}
              >
                取消
              </Button>
            </Col>
            <Col span={12}>
              <Button 
                type="primary" 
                htmlType="submit" 
                block
                style={{ height: 40, backgroundColor: '#1890ff' }}
              >
                确定
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

    </div>
    </ConfigProvider>
  );
};

export default WorkRecords;